/**
 * Shared resolution rules used by the assembler AND the active-field resolver.
 * Keeping them in one place is what guarantees the form asks for exactly the
 * fields the document will print.
 */
import {
  compare, convertArea, div, groupIndianDigits, isFilledCondition, isTier3, itemsOf, mul, parseArea, parseDec, toFixed,
  add as decAdd, sub as decSub,
  type AreaUnit, type ClauseEntry, type ClauseOption, type Condition, type ContentBlock, type Dec, type Expr, type FieldDef, type Fill,
  type InlineRun, type PartyFill, type PartySide, type Sentence, type SentenceRun, type Template,
} from '@saledeed/shared';
import { amountInWords, formatValue, isEmpty, isPresence } from './values';

export interface PartyCtx { side: PartySide; index: number; data: PartyFill }

export interface Ctx {
  fields: ReadonlyMap<string, FieldDef>;
  fill: Fill;
  party: PartyCtx | null;
  /** `side:type` -> the name field a list of names uses when the admin chose none (defaultNameFields). */
  names?: ReadonlyMap<string, string>;
}

export function fieldMap(template: Template): Map<string, FieldDef> {
  return new Map(template.fields.map(f => [f.id, f]));
}

/**
 * Each type's name, as the admin wrote it: the first plain text field in that
 * type's wording ({{NAME}} for Public, {{Bank name}} for Bank…). A list of
 * names ("by A, B and C") uses it for every type the admin did not set, so a
 * type added later is listed too. Keys are `side:type`.
 */
export function defaultNameFields(template: Pick<Template, 'blocks' | 'fields'>): Map<string, string> {
  const fields = new Map(template.fields.map(f => [f.id, f]));
  const out = new Map<string, string>();
  const isName = (id: string) => {
    const f = fields.get(id);
    return !!f && f.scope === 'party' && (f.type === 'text' || f.type === 'list');
  };
  const first = (runs: readonly (SentenceRun | InlineRun)[]): string | null => {
    for (const r of runs) {
      if (r.kind === 'fragment') { const inner = first(r.runs); if (inner) return inner; continue; }
      if (r.kind === 'placeholder' && isName(r.field)) return r.field;
    }
    return null;
  };
  const inSentences = (ss: readonly Sentence[]) => {
    for (const s of ss) { const id = first(s.runs); if (id) return id; }
    return null;
  };
  const inContent = (b: ContentBlock): string | null => {
    switch (b.type) {
      case 'heading': return first(b.sentence.runs);
      case 'paragraph': return inSentences(b.sentences);
      case 'list': for (const it of b.items) { const id = inSentences(it.sentences); if (id) return id; } return null;
      case 'table': for (const row of b.rows) for (const c of row.cells) { const id = inSentences(c.sentences); if (id) return id; } return null;
    }
  };
  for (const b of template.blocks) {
    if (b.type !== 'partyBlock') continue;
    for (const [type, blocks] of Object.entries(b.variants)) {
      const key = `${b.side}:${type}`;
      if (out.has(key)) continue;
      for (const cb of blocks) { const id = inContent(cb); if (id) { out.set(key, id); break; } }
    }
  }
  return out;
}

/** The field a list of names prints for one item: the admin's choice for its type, else its name. */
export function nameFieldFor(ctx: Ctx, side: PartySide, type: string, chosen: Readonly<Record<string, string>>): string | undefined {
  return chosen[type] ?? ctx.names?.get(`${side}:${type}`);
}

export type Resolved =
  | { kind: 'empty' }
  | { kind: 'text'; text: string }
  | { kind: 'hole' };

export type PlaceholderFormat = 'default' | 'words' | AreaUnit;

function rawValue(ctx: Ctx, def: FieldDef) {
  return def.scope === 'party' ? ctx.party?.data.values[def.id] : ctx.fill.values[def.id];
}

/* --------------------------------------------------------- formulas */

const MAX_DEPTH = 40;

/** A field's value as a number (areas converted to `unit`). Null when missing or not numeric. */
export function numericValue(ctx: Ctx, fieldId: string, unit: AreaUnit | undefined, depth = 0): Dec | null {
  const def = ctx.fields.get(fieldId);
  if (!def || depth > MAX_DEPTH) return null;
  if (def.scope === 'party' && !ctx.party) return null;
  if (def.type === 'calculated') return def.formula ? evaluate(ctx, def.formula, depth + 1) : null;
  const raw = rawValue(ctx, def);
  if (typeof raw !== 'string' || !raw.trim()) return null;
  switch (def.type) {
    case 'number':
    case 'currency':
      return parseDec(raw);
    case 'area': {
      const a = parseArea(raw);
      return a ? convertArea(a.amount, a.unit, unit ?? a.unit) : null;
    }
    default:
      return null;
  }
}

/**
 * Works a formula out with exact decimals. Null when any value it needs is
 * missing (the sentence then drops, like any empty field) or on division by 0.
 */
export function evaluate(ctx: Ctx, expr: Expr, depth = 0): Dec | null {
  if (depth > MAX_DEPTH) return null;
  if ('num' in expr) return parseDec(expr.num);
  if ('field' in expr) return numericValue(ctx, expr.field, expr.unit, depth + 1);
  if ('sum' in expr) {
    const items = itemsOf(ctx.fill, expr.side);
    if (!items.length) return null;
    let total: Dec = 0n;
    for (let index = 0; index < items.length; index++) {
      const v = numericValue({ ...ctx, party: { side: expr.side, index, data: items[index]! } }, expr.sum, expr.unit, depth + 1);
      if (v === null) return null;
      total = decAdd(total, v);
    }
    return total;
  }
  if ('op' in expr) {
    const a = evaluate(ctx, expr.a, depth + 1);
    const b = evaluate(ctx, expr.b, depth + 1);
    if (a === null || b === null) return null;
    switch (expr.op) {
      case '+': return decAdd(a, b);
      case '-': return decSub(a, b);
      case '*': return mul(a, b);
      case '/': return div(a, b);
    }
  }
  const a = evaluate(ctx, expr.if.a, depth + 1);
  const b = evaluate(ctx, expr.if.b, depth + 1);
  if (a === null || b === null) return null;
  const c = compare(a, b);
  const holds = { '>': c > 0, '>=': c >= 0, '<': c < 0, '<=': c <= 0, '=': c === 0 }[expr.if.cmp];
  return evaluate(ctx, holds ? expr.then : expr.else, depth + 1);
}

/** The decimals a calculated field keeps: its own setting, else 0 for amounts and 2 for numbers. */
export function calculatedDecimals(def: FieldDef): number {
  return def.decimals ?? (def.result === 'currency' ? 0 : 2);
}

/** How a calculated result prints: 1,51,25,000 (or in words) for amounts, 0.2500 for numbers. */
export function formatCalculated(def: FieldDef, value: Dec, format: PlaceholderFormat): string {
  const plain = toFixed(value, calculatedDecimals(def));
  if (def.result === 'currency' && format === 'words') return amountInWords(plain.replace(/^-/, '')) ?? plain;
  return groupIndianDigits(plain);
}

/* ------------------------------------------------------------ fields */

export function resolveField(ctx: Ctx, fieldId: string, format: PlaceholderFormat = 'default'): Resolved {
  const def = ctx.fields.get(fieldId);
  if (!def) return { kind: 'empty' };
  if (def.type === 'calculated') {
    if (def.scope === 'party' && !ctx.party) return { kind: 'empty' };
    const v = def.formula ? evaluate(ctx, def.formula) : null;
    return v === null ? { kind: 'empty' } : { kind: 'text', text: formatCalculated(def, v, format) };
  }
  const raw = rawValue(ctx, def);
  if (raw === undefined) return { kind: 'empty' };
  if (isPresence(raw)) return isTier3(def.type) ? { kind: 'hole' } : { kind: 'empty' };
  if (raw.trim() === '') return { kind: 'empty' };
  return { kind: 'text', text: formatValue(def, raw, format) };
}

/**
 * "Filled" is true for any non-blank value, for Tier 3 presence, and for a
 * calculation that can be worked out. "Equals" compares the raw stored value;
 * Tier 3 fields are barred from it.
 */
export function conditionHolds(ctx: Ctx, cond: Condition): boolean {
  const def = ctx.fields.get(cond.field);
  if (!def) return false;
  if (def.type === 'calculated') {
    return isFilledCondition(cond) && (def.scope !== 'party' || !!ctx.party) && !!def.formula && evaluate(ctx, def.formula) !== null;
  }
  const raw = rawValue(ctx, def);
  if (isFilledCondition(cond)) return !isEmpty(raw);
  return typeof raw === 'string' && raw.trim() === cond.equals;
}

/** Entries where every option is mandatory (standalone clauses) need no selection. */
export function isAutoSelected(entry: ClauseEntry): boolean {
  return entry.min === entry.options.length;
}

/** Selected options in the ADMIN's order, capped at max. */
export function selectedOptions(entry: ClauseEntry, fill: Fill): ClauseOption[] {
  if (isAutoSelected(entry)) return entry.options;
  const ids = new Set(fill.clauses[entry.id] ?? []);
  return entry.options.filter(o => ids.has(o.id)).slice(0, entry.max);
}

export function joinNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]!}`;
}
